import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import useFocusMode from "../hooks/useFocusMode";
import { useUser } from "../contexts/UserContext";

/** Routes that are always accessible regardless of focus profile */
const ALWAYS_ALLOWED = ["/account", "/admin", "/pick-focus", "/tip-us", "/subscriptionSuccess", "/badges", "/pet", "/community", "/family", "/collection"];

/**
 * Wraps a route element and redirects to home if focus mode is active
 * and the current path isn't part of the user's focus profile.
 */
const FocusGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { focusModeEnabled, profile } = useFocusMode();
  const location = useLocation();
  const userContext = useUser();
  const isAdmin = userContext?.userDetails?.admin === true;
  const isTester = userContext?.userDetails?.tester === true;

  // Admins/testers see all nav items, so don't restrict their routes
  if (isAdmin || isTester) return children;

  if (!focusModeEnabled || !profile) return children;

  const pathname = location.pathname;

  // Always allow certain utility routes
  if (ALWAYS_ALLOWED.some((p) => pathname.startsWith(p))) return children;

  // Check if this route matches any tab in the focus profile
  const allowed = profile.tabNav.some((tab) => {
    const tabPath = tab.path.split("?")[0]; // strip query params
    // Exact match or sub-route match (e.g. /notes/note/123 matches /notes/)
    return pathname === tabPath
      || pathname.startsWith(tabPath) && tabPath !== "/"
      || tabPath.startsWith(pathname) && pathname !== "/";
  });

  if (!allowed) return <Navigate to="/" replace />;

  return children;
};

export default FocusGuard;
